import React, { useEffect, useRef, useState } from "react"
import { Link } from "gatsby"
import { imgixUrl } from "../utils/imgix"

// Éditorial SSG du hub Séjours (Phase 1 GEO) : chapitres destinations +
// « L'essentiel » + FAQ. Tout est rendu dans le HTML statique — les crawlers
// IA (Google AI, Copilot) n'exécutent pas le JS, ce contenu est LEUR page.
// Chiffres réels relevés dans l'index Typesense le 04/09/2026 — à rafraîchir
// avec la FAQ si l'offre change fortement (ordre de grandeur, pas au bien près).
// Sections photographiques sombres dans les DEUX thèmes (standard v3.5) ;
// « L'essentiel » est clair avec des règles gd-night dédiées.

const IMG = {
  alps: "https://www.datocms-assets.com/33658/1777818178-cimalpes-moon-1850-courchevel-01.jpg",
  greece: "https://www.datocms-assets.com/33658/1787858409-lmparos-kitma-estate-01.jpg",
  ocean: "https://www.datocms-assets.com/33658/1787922508-dsc6866-copy.jpg",
}

const DESTINATIONS = {
  fr: {
    eyebrow: "Destinations",
    title: "L'hiver aux sommets. L'été en mer Égée.",
    intro:
      "Chaque maison est sélectionnée, visitée et suivie par un conseiller GADAIT — des chalets skis aux pieds de Courchevel aux villas privées des Cyclades.",
    base: "/fr/locations-vacances/",
    chapters: [
      {
        key: "alps",
        wide: true,
        eyebrow: "Alpes françaises",
        title: "Chalets et appartements, skis aux pieds.",
        stat: "312 séjours · dès 500 € la nuit",
        alt: "Salon d'un chalet de luxe à Courchevel, cheminée et vue sur les sommets",
        href: "/fr/locations-vacances/france-country/",
        chips: [
          { label: "Courchevel", href: "/fr/location-chalet-courchevel/" },
          { label: "Megève", href: "/fr/location-chalet-megeve/" },
          { label: "Méribel", href: "/fr/location-chalet-meribel/" },
          {
            label: "Saint-Martin-de-Belleville",
            href: "/fr/location-chalet-saint-martin-de-belleville/",
          },
        ],
      },
      {
        key: "greece",
        eyebrow: "Grèce",
        title: "Villas privées face à la mer Égée.",
        stat: "73 séjours · dès 612 € la nuit",
        alt: "Domaine privé avec piscines face à la mer Égée à Paros",
        // Slugs de facettes localisés : la collection FR stocke « Grèce ».
        href: "/fr/locations-vacances/grece-country/",
        chips: [
          { label: "Paros", href: "/fr/location-villa-paros/" },
          { label: "Porto Heli", href: "/fr/location-villa-porto-heli/" },
        ],
      },
      {
        key: "ocean",
        eyebrow: "Océan Indien",
        title: "Maurice et Seychelles, pieds dans l'eau.",
        stat: "8 séjours · villas avec service",
        alt: "Piscine privée d'une villa tropicale à l'île Maurice",
        href: "/fr/locations-vacances/ile-maurice-country/",
        chips: [
          {
            label: "Longue durée à Maurice",
            href: "/fr/location-longue-duree-maurice/",
          },
        ],
      },
    ],
  },
  en: {
    eyebrow: "Destinations",
    title: "Winter on the slopes. Summer on the Aegean.",
    intro:
      "Every home is selected, visited and managed with a GADAIT advisor — from ski-in chalets in Courchevel to private villas in the Cyclades.",
    base: "/en/holiday-rentals/",
    chapters: [
      {
        key: "alps",
        wide: true,
        eyebrow: "French Alps",
        title: "Chalets and apartments, ski-in ski-out.",
        stat: "312 stays · from €500 a night",
        alt: "Living room of a luxury Courchevel chalet with fireplace and mountain views",
        href: "/en/holiday-rentals/france-country/",
        chips: [
          { label: "Courchevel", href: "/en/chalet-rental-courchevel/" },
          { label: "Megève", href: "/en/chalet-rental-megeve/" },
          { label: "Méribel", href: "/en/chalet-rental-meribel/" },
          {
            label: "Saint-Martin-de-Belleville",
            href: "/en/chalet-rental-saint-martin-de-belleville/",
          },
        ],
      },
      {
        key: "greece",
        eyebrow: "Greece",
        title: "Private villas facing the Aegean.",
        stat: "73 stays · from €612 a night",
        alt: "Private estate with pools overlooking the Aegean Sea in Paros",
        href: "/en/holiday-rentals/greece-country/",
        chips: [
          { label: "Paros", href: "/en/villa-rental-paros/" },
          { label: "Porto Heli", href: "/en/villa-rental-porto-heli/" },
        ],
      },
      {
        key: "ocean",
        eyebrow: "Indian Ocean",
        title: "Mauritius and the Seychelles, on the water.",
        stat: "8 stays · serviced villas",
        alt: "Private pool of a tropical villa in Mauritius",
        href: "/en/holiday-rentals/mauritius-country/",
        chips: [
          {
            label: "Long-term in Mauritius",
            href: "/en/long-term-rentals-mauritius/",
          },
        ],
      },
    ],
  },
}


// Révélation en cascade (JS pur au-dessus du SSG : sans JS ou en
// prefers-reduced-motion, rien n'est jamais caché — la classe hd-js qui
// active les styles d'entrée n'est posée qu'ici). Garde-fou temporel 2,5 s.
const useCascadeReveal = () => {
  const rootRef = useRef(null)
  useEffect(() => {
    const root = rootRef.current
    if (!root || typeof window === "undefined") return undefined
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches)
      return undefined
    if (!("IntersectionObserver" in window)) return undefined
    root.classList.add("hd-js")
    const targets = Array.from(root.querySelectorAll("[data-hd-reveal]"))
    targets.forEach((el, index) => {
      el.style.transitionDelay = `${(index % 6) * 90}ms`
    })
    const io = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add("hd-in")
            io.unobserve(entry.target)
          }
        })
      },
      { rootMargin: "0px 0px -10% 0px", threshold: 0.15 }
    )
    targets.forEach(el => io.observe(el))
    const failsafe = window.setTimeout(
      () => targets.forEach(el => el.classList.add("hd-in")),
      2500
    )
    return () => {
      io.disconnect()
      window.clearTimeout(failsafe)
    }
  }, [])
  return rootRef
}

const REVEAL_STYLE = `
  .hd-js [data-hd-reveal] {
    opacity: 0;
    transform: translateY(12px);
    transition: opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1),
      transform 0.7s cubic-bezier(0.22, 1, 0.36, 1);
  }
  .hd-js [data-hd-reveal].hd-in {
    opacity: 1;
    transform: none;
  }
  @media (prefers-reduced-motion: reduce) {
    .hd-js [data-hd-reveal] {
      opacity: 1;
      transform: none;
      transition: none;
    }
  }
`

const ChapterCard = ({ chapter }) => (
  <Link
    to={chapter.href}
    className={`group relative block overflow-hidden rounded-[28px] bg-[#101017] ${
      chapter.wide ? "md:col-span-2" : ""
    }`}
  >
    <div className={chapter.wide ? "aspect-[16/9] md:aspect-[21/9]" : "aspect-[16/10]"}>
      <img
        src={imgixUrl(IMG[chapter.key], "auto=format,compress&q=60&w=1600")}
        srcSet={`${imgixUrl(IMG[chapter.key], "auto=format,compress&q=60&w=900")} 900w, ${imgixUrl(
          IMG[chapter.key],
          "auto=format,compress&q=60&w=1600"
        )} 1600w`}
        sizes={chapter.wide ? "(min-width: 768px) 80rem, 100vw" : "(min-width: 768px) 40rem, 100vw"}
        alt={chapter.alt}
        loading="lazy"
        className="h-full w-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-[1.04]"
      />
    </div>
    <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#05060c]/90 via-[#05060c]/35 to-transparent" />
    <div className="absolute inset-x-0 bottom-0 p-6 [text-shadow:0_1px_14px_rgba(0,0,0,0.55)] md:p-8">
      <p className="text-[10.5px] font-semibold uppercase tracking-[0.24em] text-white/70">
        {chapter.eyebrow}
      </p>
      <h3 className="mt-2 max-w-[24ch] text-[clamp(20px,2vw,28px)] font-semibold leading-tight tracking-[-0.03em] text-white">
        {chapter.title}
      </h3>
      <p className="mt-2 text-[13px] tabular-nums text-white/65">{chapter.stat}</p>
    </div>
  </Link>
)

export const HolidayDestinations = ({ locale = "en" }) => {
  const copy = DESTINATIONS[locale] || DESTINATIONS.en
  const revealRef = useCascadeReveal()
  return (
    <section
      ref={revealRef}
      aria-label={copy.eyebrow}
      className="bg-[#0a0a0e] px-6 py-16 md:py-20"
    >
      <style>{REVEAL_STYLE}</style>
      <div className="mx-auto max-w-7xl">
        <div data-hd-reveal className="flex items-center gap-3">
          <span className="block h-px w-5 bg-[#0071e3]" />
          <p className="text-[10px] font-medium uppercase tracking-[0.28em] text-[#6ea8ff]">
            {copy.eyebrow}
          </p>
        </div>
        <h2
          data-hd-reveal
          className="mt-4 max-w-[22ch] text-[clamp(1.8rem,3.4vw,3rem)] font-semibold leading-[1.05] tracking-[-0.04em] text-white"
        >
          {copy.title}
        </h2>
        <p data-hd-reveal className="mt-4 max-w-[46rem] text-[15px] leading-7 text-white/60">
          {copy.intro}
        </p>
        <div className="mt-10 grid gap-6 md:grid-cols-2">
          {copy.chapters.map(chapter => (
            <div
              key={chapter.key}
              data-hd-reveal
              className={chapter.wide ? "md:col-span-2" : ""}
            >
              <ChapterCard chapter={chapter} />
              <div className="mt-3.5 flex flex-wrap gap-2">
                {chapter.chips.map(chip => (
                  <Link
                    key={chip.href}
                    to={chip.href}
                    className="rounded-full border border-white/[0.14] px-3.5 py-1.5 text-[12px] font-medium text-white/65 transition hover:border-white/35 hover:text-white"
                  >
                    {chip.label}
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

// ── « L'essentiel » + FAQ — la matière citable, en clair, thémée nuit ──

const ESSENTIALS = {
  fr: {
    eyebrow: "L'essentiel",
    title: "Les séjours GADAIT, en bref.",
    brief:
      "GADAIT International propose 361 séjours de luxe dans 4 pays (relevé du 6 septembre 2026) : 312 chalets et appartements dans les Alpes françaises — dont 166 à Courchevel, ainsi qu'à Megève, Méribel et Saint-Martin-de-Belleville —, 73 villas privées en Grèce entre Paros et Porto Heli, et une sélection de villas avec service à l'île Maurice et aux Seychelles. Les tarifs affichés démarrent à 500 € la nuit dans les Alpes (nuit médiane à Courchevel : environ 1 400 €) et à 612 € la nuit en Grèce ; une partie de la collection est proposée sur demande. Chaque réservation passe par un conseiller dédié qui confirme la disponibilité, les services de la maison et l'organisation du séjour.",
    stats: [
      { value: "393", n: 393, tpl: "%s", label: "séjours en collection" },
      { value: "4", n: 4, tpl: "%s", label: "pays" },
      { value: "500 €", n: 500, tpl: "%s €", label: "la nuit, à partir de" },
      { value: "13", n: 13, tpl: "%s", label: "chambres au maximum" },
    ],
    faqTitle: "Questions fréquentes",
    faq: [
      {
        q: "Combien coûte la location d'un chalet de luxe à Courchevel ?",
        more: "/fr/prix-location-chalet-courchevel/",
        a: "Sur les tarifs affichés début septembre 2026, les chalets et appartements de la collection à Courchevel démarrent à 500 € la nuit, avec une nuit médiane autour de 1 400 € et jusqu'à environ 23 600 € la nuit pour les chalets d'exception. Une grande partie des maisons les plus rares est proposée sur demande : votre conseiller confirme le tarif exact selon les dates.",
      },
      {
        q: "Quelles destinations couvre la collection de séjours ?",
        a: "Quatre pays : la France (Courchevel, Megève, Méribel, Saint-Martin-de-Belleville et d'autres stations des Alpes), la Grèce (43 villas à Paros et 27 à Porto Heli), l'île Maurice et les Seychelles.",
      },
      {
        q: "Les séjours incluent-ils des services ?",
        more: "/fr/services-chalet-luxe-inclus/",
        a: "Les services varient selon les maisons : la plupart des chalets alpins sont opérés avec accueil et entretien, et certains proposent chef privé, spa ou conciergerie dédiée. Le détail figure sur chaque fiche, et votre conseiller compose le séjour (transferts, chef, expériences) selon vos attentes.",
      },
      {
        q: "Comment vérifier la disponibilité d'une maison ?",
        a: "Depuis chaque bien, indiquez vos dates d'arrivée et de départ et le nombre de voyageurs : votre conseiller confirme la disponibilité et le tarif dans l'heure ouvrée, par email ou téléphone selon votre préférence.",
      },
      {
        q: "Quand réserver pour un séjour d'hiver à Courchevel ?",
        more: "/fr/quand-reserver-chalet-courchevel/",
        a: "Les semaines de Noël et du Nouvel An sont réservées en premier, souvent plusieurs mois à l'avance. Pour disposer du choix complet sur les grandes maisons, l'idéal est de confirmer entre septembre et octobre ; janvier et mars offrent davantage de flexibilité.",
      },
      {
        q: "Peut-on louer pour une longue durée ?",
        more: "/fr/location-longue-duree-maurice/",
        a: "Oui. À l'île Maurice, une collection dédiée à la location longue durée est disponible, et certaines maisons acceptent les séjours prolongés — votre conseiller vérifie les conditions maison par maison.",
      },
    ],
  },
  en: {
    eyebrow: "The essentials",
    title: "GADAIT stays, in brief.",
    brief:
      "GADAIT International curates 361 luxury stays across 4 countries (as of 6 September 2026): 312 chalets and apartments in the French Alps — 166 of them in Courchevel, plus Megève, Méribel and Saint-Martin-de-Belleville —, 73 private villas in Greece between Paros and Porto Heli, and a selection of serviced villas in Mauritius and the Seychelles. Displayed rates start at €500 a night in the Alps (median Courchevel night: about €1,400) and €612 a night in Greece; part of the collection is priced on request. Every booking is handled by a dedicated advisor who confirms availability, each home's services and the full arrangement of the stay.",
    stats: [
      { value: "393", n: 393, tpl: "%s", label: "stays in the collection" },
      { value: "4", n: 4, tpl: "%s", label: "countries" },
      { value: "€500", n: 500, tpl: "€%s", label: "a night, from" },
      { value: "13", n: 13, tpl: "%s", label: "bedrooms at most" },
    ],
    faqTitle: "Frequently asked questions",
    faq: [
      {
        q: "How much does a luxury chalet rental in Courchevel cost?",
        more: "/en/courchevel-chalet-rental-cost/",
        a: "On rates displayed in early September 2026, chalets and apartments in the Courchevel collection start at €500 a night, with a median night around €1,400 and up to roughly €23,600 a night for the most exceptional chalets. Many of the rarest homes are priced on request: your advisor confirms the exact rate for your dates.",
      },
      {
        q: "Which destinations does the holiday collection cover?",
        a: "Four countries: France (Courchevel, Megève, Méribel, Saint-Martin-de-Belleville and other Alpine resorts), Greece (43 villas in Paros and 27 in Porto Heli), Mauritius and the Seychelles.",
      },
      {
        q: "Do the stays include services?",
        more: "/en/luxury-chalet-services-included/",
        a: "Services vary by home: most Alpine chalets are operated with welcome and housekeeping, and some offer a private chef, spa or dedicated concierge. Each listing details what is included, and your advisor arranges the stay — transfers, chef, experiences — around your plans.",
      },
      {
        q: "How do I check a home's availability?",
        a: "From any listing, share your arrival and departure dates and the number of guests: your advisor confirms availability and the rate within the working hour, by email or phone as you prefer.",
      },
      {
        q: "When should I book a winter stay in Courchevel?",
        more: "/en/when-book-chalet-courchevel/",
        a: "Christmas and New Year weeks are booked first, often months ahead. For the full choice of the larger homes, confirm between September and October; January and March offer more flexibility.",
      },
      {
        q: "Can I rent for a longer period?",
        more: "/en/long-term-rentals-mauritius/",
        a: "Yes. In Mauritius a dedicated long-term rental collection is available, and several homes accept extended stays — your advisor checks the conditions home by home.",
      },
    ],
  },
}

const FaqItem = ({ item, open, onToggle, moreLabel }) => (
  <div className="hd-faq-item border-b border-black/8">
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      className="flex w-full items-center justify-between gap-6 py-5 text-left"
    >
      <span className="hd-faq-q text-[15px] font-semibold tracking-[-0.01em] text-[#111827]">
        {item.q}
      </span>
      <span
        aria-hidden="true"
        className={`hd-faq-chev shrink-0 text-[13px] text-[#6e6e73] transition-transform duration-300 ${
          open ? "rotate-45" : ""
        }`}
      >
        +
      </span>
    </button>
    {/* La réponse reste TOUJOURS dans le DOM (HTML statique pour les
        crawlers) ; l'ouverture anime grid-template-rows 0fr→1fr. */}
    <div
      className={`hd-faq-body ${open ? "hd-faq-open" : ""}`}
      aria-hidden={!open}
    >
      <div className="hd-faq-inner">
        <p className="hd-faq-a max-w-[62rem] pb-5 text-[14px] leading-7 text-[#4b4b52]">
          {item.a}
        </p>
        {item.more ? (
          <Link
            to={item.more}
            tabIndex={open ? 0 : -1}
            className="mb-5 mt-[-8px] inline-block text-[13.5px] font-medium text-[#0071e3] hover:underline"
          >
            {moreLabel}
          </Link>
        ) : null}
      </div>
    </div>
  </div>
)

export const HolidayEssentials = ({ locale = "en" }) => {
  const copy = ESSENTIALS[locale] || ESSENTIALS.en
  const [openIndex, setOpenIndex] = useState(0)
  const revealRef = useCascadeReveal()

  // Compteurs des tuiles : le SSG rend la valeur finale ; le JS anime
  // 0→n (~900 ms, easing sortant) à l'entrée à l'écran, valeur exacte
  // toujours posée à la fin. prefers-reduced-motion ⇒ rien.
  useEffect(() => {
    const root = revealRef.current
    if (!root || typeof window === "undefined") return undefined
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches)
      return undefined
    if (!("IntersectionObserver" in window)) return undefined
    const nodes = Array.from(root.querySelectorAll("[data-hd-count]"))
    if (!nodes.length) return undefined
    const timers = []
    const animate = el => {
      const n = Number(el.getAttribute("data-hd-count") || 0)
      const tpl = el.getAttribute("data-hd-tpl") || "%s"
      const sep = locale === "fr" ? "\u202f" : ","
      const fmt = v =>
        tpl.replace("%s", String(v).replace(/\B(?=(\d{3})+(?!\d))/g, sep))
      const start = performance.now()
      const tick = now => {
        const t = Math.min(1, (now - start) / 900)
        const eased = 1 - Math.pow(1 - t, 3)
        el.textContent = fmt(Math.round(n * eased))
        if (t < 1) timers.push(requestAnimationFrame(tick))
        else el.textContent = fmt(n)
      }
      timers.push(requestAnimationFrame(tick))
    }
    const io = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            animate(entry.target)
            io.unobserve(entry.target)
          }
        })
      },
      { threshold: 0.4 }
    )
    nodes.forEach(el => io.observe(el))
    // garde-fou : valeur finale exacte quoi qu'il arrive après 2,5 s
    const failsafe = window.setTimeout(() => {
      nodes.forEach(el => {
        const tpl = el.getAttribute("data-hd-tpl") || "%s"
        el.textContent = tpl.replace("%s", el.getAttribute("data-hd-count"))
      })
    }, 2500)
    return () => {
      io.disconnect()
      timers.forEach(id => cancelAnimationFrame(id))
      window.clearTimeout(failsafe)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <section
      ref={revealRef}
      aria-label={copy.eyebrow}
      className="hd-essentials bg-[#f5f5f7] px-6 py-16 md:py-20"
    >
      <style>{REVEAL_STYLE}</style>
      <div className="mx-auto max-w-7xl">
        <div data-hd-reveal className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#8d8d92]">
          {copy.eyebrow}
        </div>
        <h2
          data-hd-reveal
          className="hd-title mt-3 text-[2rem] font-semibold tracking-[-0.045em] text-[#111827] md:text-[2.5rem]"
        >
          {copy.title}
        </h2>
        <p data-hd-reveal className="hd-brief mt-5 max-w-[62rem] text-[15px] leading-[1.85] text-[#3d3d43]">
          {copy.brief}
        </p>
        <div className="mt-10 grid grid-cols-2 gap-4 md:grid-cols-4">
          {copy.stats.map(stat => (
            <div
              key={stat.label}
              data-hd-reveal
              className="hd-stat rounded-[22px] border border-black/6 bg-white px-5 py-5"
            >
              <div
                className="hd-stat-v text-[1.7rem] font-semibold tracking-[-0.04em] text-[#111827] tabular-nums"
                data-hd-count={stat.n}
                data-hd-tpl={stat.tpl}
              >
                {stat.value}
              </div>
              <div className="mt-1 text-[12px] leading-5 text-[#6e6e73]">
                {stat.label}
              </div>
            </div>
          ))}
        </div>
        <h3 className="hd-title mt-14 text-[1.35rem] font-semibold tracking-[-0.03em] text-[#111827]">
          {copy.faqTitle}
        </h3>
        <div className="mt-4 border-t border-black/8">
          {copy.faq.map((item, index) => (
            <FaqItem
              key={item.q}
              item={item}
              open={openIndex === index}
              onToggle={() => setOpenIndex(openIndex === index ? -1 : index)}
              moreLabel={
                locale === "fr" ? "Lire la réponse complète →" : "Read the full answer →"
              }
            />
          ))}
        </div>
      </div>
      <style>{`
        .hd-faq-body {
          display: grid;
          grid-template-rows: 0fr;
          transition: grid-template-rows 0.38s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .hd-faq-body.hd-faq-open {
          grid-template-rows: 1fr;
        }
        .hd-faq-inner {
          overflow: hidden;
          min-height: 0;
        }
        @media (prefers-reduced-motion: reduce) {
          .hd-faq-body {
            transition: none;
          }
        }
        html.gd-night-mode .hd-essentials {
          background: #0a0a0e;
        }
        html.gd-night-mode .hd-essentials .hd-title,
        html.gd-night-mode .hd-essentials .hd-stat-v,
        html.gd-night-mode .hd-essentials .hd-faq-q {
          color: #f5f5f7;
        }
        html.gd-night-mode .hd-essentials .hd-brief,
        html.gd-night-mode .hd-essentials .hd-faq-a {
          color: rgba(245, 245, 247, 0.62);
        }
        html.gd-night-mode .hd-essentials .hd-stat {
          background: #141419;
          border-color: rgba(255, 255, 255, 0.09);
        }
        html.gd-night-mode .hd-essentials .hd-faq-item {
          border-color: rgba(255, 255, 255, 0.1);
        }
      `}</style>
    </section>
  )
}

// FAQ exportée pour le schema FAQPage des pages hub (source unique).
export const HOLIDAY_FAQ = {
  fr: ESSENTIALS.fr.faq,
  en: ESSENTIALS.en.faq,
}
